import { ExternalScheduleConcreteDirectInputsProps } from './types';
import { FormControl, FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/form/select';
import { Input } from '@components/ui/form/input';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useDropdownsContext } from '@contexts/dropdowns';

export const ExternalScheduleConcreteDirectInputs = ({
  form,
  disabled,
  address,
  geolocalization,
  children,
}: ExternalScheduleConcreteDirectInputsProps) => {
  const { sites, pourMethods, pourElements, concreteMix, additive, rateTypes } =
    useDropdownsContext();

  const { t } = useTranslation('app', { keyPrefix: 'forms' });

  const { control, watch, setValue } = form;
  const {
    rateType,
    additive: { itemExternalId: additiveItemExternalId },
  } = watch();

  return (
    <>
      <FormField
        control={control}
        name="externalSiteId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('externalSiteId.label')} select required>
            <Select value={value} onValueChange={onChange} disabled={sites.disabled || disabled}>
              <FormControl>
                <SelectTrigger name="externalSiteId" disabled={sites.disabled || disabled}>
                  <SelectValue id="externalSiteId" placeholder={t('default.select.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {sites.options.map(({ externalSiteId: id, siteName }) => (
                  <SelectItem key={id} value={id}>
                    {siteName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItemWrapper>
        )}
      />
      <FormItemWrapper label={t('address.label')}>
        <Input readOnly disabled value={address} />
      </FormItemWrapper>
      <div className="flex gap-4">
        <FormItemWrapper label={t('geolocalization.label')}>
          <Input readOnly disabled value={geolocalization} />
        </FormItemWrapper>
      </div>
      {children}
      <div className="flex gap-4">
        <FormField
          control={control}
          name="id"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormItemWrapper label={t('itemExternalId.label')} select required>
              <MultiSelect
                className="font-normal"
                error={error}
                value={value}
                onChange={(v) => {
                  onChange(v);
                  const selected = concreteMix.options.find((el) => el.id === v[0]);
                  if (!selected) return;
                  setValue('itemExternalId', selected.itemExternalId);
                  setValue('slump', selected?.defaultSlump || '');
                }}
                placeholder={t('default.select.placeholder')}
                disabled={concreteMix.disabled || disabled}
                singleMode
                options={concreteMix.options.map((el) => ({
                  value: el.id,
                  label: el.description,
                }))}
              />
            </FormItemWrapper>
          )}
        />
      </div>
      <div className="flex items-start gap-4">
        <FormField
          control={control}
          name="quantity"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper className="grow" label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                className="w-full"
                disabled={disabled}
                suffixElement={<strong>cy</strong>}
                {...field}
              />
            </FormItemWrapper>
          )}
        />
        <div className="flex grow items-end gap-4">
          <FormField
            control={control}
            name="isExactQuantity"
            render={({ field: { value, onChange } }) => (
              <RadioGroup
                name="isExactQuantity"
                className="flex h-8 items-center gap-3 text-xs"
                defaultValue={value ? 'exact' : 'estimated'}
                disabled={disabled}
                onValueChange={(v) => onChange(v === 'exact')}>
                <span className="flex items-center gap-2">
                  <RadioGroupItem id="exact" value="exact" />
                  <label htmlFor="exact">{t('exact.label')}</label>
                </span>
                <span className="flex items-center gap-2">
                  <RadioGroupItem id="estimated" value="estimated" />
                  <label htmlFor="estimated">{t('estimated.label')}</label>
                </span>
              </RadioGroup>
            )}
          />
          <FormField
            control={control}
            name="slump"
            render={({ field }) => (
              <FormItemWrapper label={t('estimatedSlump.label')}>
                <Input type="number" disabled={disabled} className="w-32" {...field} />
              </FormItemWrapper>
            )}
          />
        </div>
      </div>
      <FormField
        control={control}
        name="additive.id"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('additive.label')} select required>
            <MultiSelect
              className="font-normal"
              value={value}
              onChange={(v) => {
                onChange(v);
                const selected = additive.options.find((el) => el.id === v[0]);
                setValue('additive.itemExternalId', selected?.itemExternalId || '');
                if (!selected) setValue('additive.quantity', '');
              }}
              placeholder={t('default.select.placeholder')}
              disabled={additive.disabled || disabled}
              singleMode
              options={additive.options.map((el) => ({
                value: el.id,
                label: el.description,
              }))}
            />
          </FormItemWrapper>
        )}
      />
      {additiveItemExternalId && (
        <FormField
          control={control}
          name="additive.quantity"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper className="basis-full" label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                disabled={disabled}
                className="w-full"
                {...field}
              />
            </FormItemWrapper>
          )}
        />
      )}

      <div className="flex gap-4">
        <FormField
          control={control}
          name="pourMethod"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('pourMethod.label')} select required>
              <Select
                value={value}
                onValueChange={onChange}
                disabled={pourMethods.disabled || disabled}>
                <FormControl>
                  <SelectTrigger name="pourMethod" disabled={pourMethods.disabled || disabled}>
                    <SelectValue id="pourMethod" placeholder={t('default.select.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {pourMethods.options.map(({ id, value: v, name }) => (
                    <SelectItem key={id} value={v}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="pourElement"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('pourElement.label')} select required>
              <Select
                value={value}
                onValueChange={onChange}
                disabled={pourElements.disabled || disabled}>
                <FormControl>
                  <SelectTrigger name="pourElement" disabled={pourElements.disabled || disabled}>
                    <SelectValue id="pourElement" placeholder={t('default.select.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {pourElements.options.map(({ id, value: v, name }) => (
                    <SelectItem key={id} value={v}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItemWrapper>
          )}
        />
      </div>
      <div className="flex gap-4">
        <FormField
          control={control}
          name="rateType"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('rateType.label')} select>
              <Select
                value={value}
                onValueChange={onChange}
                disabled={rateTypes.disabled || disabled}>
                <FormControl>
                  <SelectTrigger name="rateType" disabled={rateTypes.disabled || disabled}>
                    <SelectValue id="rateType" placeholder={t('default.select.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {rateTypes.options.map((el) => (
                    <SelectItem key={el} value={el}>
                      {t(`rateType.${el}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="quantityRate"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                className="w-full"
                disabled={disabled}
                suffixElement={<strong>{rateType === 'QTY_PER_HOUR' ? 'hours' : 'mins'}</strong>}
                {...field}
              />
            </FormItemWrapper>
          )}
        />
      </div>
      <FormField
        control={control}
        name="additionalInformation"
        render={({ field }) => (
          <FormItemWrapper label={t('additionalInfo.label')}>
            <Input disabled={disabled} {...field} />
          </FormItemWrapper>
        )}
      />
      <FormField
        control={control}
        name="note"
        render={({ field }) => (
          <FormItemWrapper label={t('internalNote.label')}>
            <Input disabled={disabled} {...field} />
          </FormItemWrapper>
        )}
      />
    </>
  );
};
